import { Log } from '@/appinsights/logging/log';
import {
	ADD_GA4_EVENT,
	ADD_GA4_IMMEDIATE_EVENT,
	ADD_GA4_PAGE_EVENTS,
	FIRE_GA4_EVENTS,
} from '@/store/modules/trackingContext';
import store from '@/store/store';
import { getBrand, getQueryParameter, isApp, isDevelop } from './environmentService';

const doTrack = ['/accept', '/receipt'];
const pageLoadWait = 1250;
const urlPollingInterval = 500;

export enum STEP_TYPE {
	START = 'trackDigitalAcceptStart',
	PROGRESS = 'trackDigitalAcceptProgress',
	SELECT = 'trackDigitalAcceptSelect',
	COMPLETE = 'trackDigitalAcceptComplete',
}

export const dataLayer = (window as any).dataLayer || [];

export const handleNavigationTracking = () => {
	try {
		setTimeout(() => {
			if (store.state.trackingContext.lastUrl !== location.href) {
				const href = location.href.toLocaleLowerCase();
				const doTrackPage = doTrack.some((trackPage) => href.includes(trackPage));
				if (doTrackPage) {
					fireTrackingEvents();
				}
			}
		}, pageLoadWait);
	} catch (e) {
		Log.exception('Error in handleNavigationTracking', e, { err: e.toString() });
		// Too bad
	}
};

const trackingFallback = () => {
	try {
		setInterval(() => {
			if (store.state.trackingContext.lastUrl !== location.href) {
				handleNavigationTracking();
			}
		}, urlPollingInterval);
	} catch (e) {
		Log.exception('Error in trackingFallback', e, { err: e.toString() });
		// Too bad
	}
};

try {
	if ((window as any).navigation) {
		// chrome, Edge
		(window as any).navigation.addEventListener('navigate', () => {
			handleNavigationTracking();
		});
	} else {
		Log.warning('UrlNavigation API not supported, falling back to UrlPolling');
		// Firefox, safari
		trackingFallback();
	}
} catch (e) {
	Log.exception('Error in navigation tracking setup', e, { err: e.toString() });
	// ?
	trackingFallback();
}

const getEventType = (): string => {
	// if lastUrl is empty, it means pageLoad event is not fired yet, so we need to wait firing this event to make sure it's tracked in the right order
	return store.state.trackingContext.lastUrl === '' ? ADD_GA4_EVENT : ADD_GA4_IMMEDIATE_EVENT;
};

export const trackGeneralPageView = async (
	from: string,
	to: string,
	pathName: string,
	offerId: string,
	offerIdStatus: string
): Promise<void> => {
	try {
		const event = {
			pageSection: pathName,
			brand: getBrand(),
			platform: isApp ? 'app' : 'web',
			previousPageUrl: from,
			fullUrl: to,
			offerId,
			offerIdStatus,
		};

		const event1 = {
			event: 'trackPageview',
		};
		await store.dispatch(ADD_GA4_PAGE_EVENTS, [event, event1]);
	} catch (e) {
		// too bad
		Log.exception('Error in trackGeneralPageView', e, { err: e.toString() });
	}
};

export const trackStep = async (
	acceptStep: { title: string; description?: string; index?: number },
	stepType: STEP_TYPE
): Promise<void> => {
	const event = {
		event: stepType,
		digitalAcceptStep: acceptStep.title,
		digitalAcceptStepInfo: acceptStep.description ? acceptStep.description : acceptStep.title,
		// DEBUG offerId: store.state.ProposalContext.quote_id, // KLK shouldn't this be added?
		insuranceData: {
			insurance: store.state.ProposalContext.policyListSorted.join(', '),
			insuranceCount: store.state.ProposalContext.policyListSorted.length,
		},
	};

	if (stepType !== STEP_TYPE.COMPLETE) {
		event['index'] = acceptStep.index;
	}
	await store.dispatch(getEventType(), event);
};

export const trackSignicat = async (start: boolean): Promise<void> => {
	const eventName = start ? 'trackDigitalSignatureStart' : 'trackDigitalSignatureComplete';

	const event = {
		event: eventName,
		digitalSignatureContext: 'digital accept',
		digitalSignatureType: 'signicat',
		// DEBUG offerId: store.state.ProposalContext.quote_id, // KLK shouldn't this be added?
		insuranceData: {
			insurance: store.state.ProposalContext.policyListSorted.join(', '),
			insuranceCount: store.state.ProposalContext.policyListSorted.length,
		},
	};

	await store.dispatch(getEventType(), event);
};

export const trackContact = async (): Promise<void> => {
	const event = {
		event: 'trackContact',
		offerId: store.state.ProposalContext.quote_id,
	};
	await store.dispatch(getEventType(), event);
};

export const trackError = async (errorType: string, errorCode = 'NA'): Promise<void> => {
	try {
		const event = {
			event: 'trackError',
			errorType,
			errorCode,
			offerId: store.state.ProposalContext.quote_id,
		};
		await store.dispatch(getEventType(), event);
	} catch (e) {
		// too bad
		Log.exception('Error in trackError', e, { err: e.toString() });
	}
};

export const initialPageTrack = () => {
	// only relevant on direct pageLoad
	setTimeout(() => {
		if (!store.state.trackingContext.isFirstPageEventFired) {
			Log.information('FirstPageLoad Fired From code');
			fireTrackingEvents();
		}
	}, 1500);
};

/***** private methods *****************/

const fireTrackingEvents = async () => {
	const isReceiptPage = location.pathname.toLocaleLowerCase().includes('/receipt');
	const offerIdStatus = isReceiptPage ? 'accepted' : 'in_progress';

	let offerId = store.state.ProposalContext.quote_id;
	if (!offerId) {
		offerId = getQueryParameter('quote');
	}
	if (!offerId && isDevelop) {
		offerId = 'MOCK_Track123456';
	}

	await trackGeneralPageView(
		store.state.trackingContext.lastUrl,
		location.href,
		location.pathname,
		offerId,
		offerIdStatus
	);
	store.state.trackingContext.lastUrl = location.href;
	await store.dispatch(FIRE_GA4_EVENTS);
};
